import { ChevronUpIcon, ChevronDownIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { baseFieldClasses } from '../../components/fieldClasses';
import { ELASTIC_SIZE_OPTIONS, ELASTIC_WEAR_OPTIONS, placementColor } from './model';
import type { ElasticPlacement } from './model';
import { toothWithSide } from './persistence';

interface PlacedElasticsListProps {
    placements: ElasticPlacement[];
    readOnly: boolean;
    /** Whether a band is currently being drawn (hides "Add elastic"). */
    drawing: boolean;
    onStartDrawing: () => void;
    onClearAll: () => void;
    onRemove: (id: string) => void;
    onSizeChange: (id: string, size: string) => void;
    onWearChange: (id: string, wear: string) => void;
    onNameChange: (id: string, name: string) => void;
    onReorder: (id: string, direction: -1 | 1) => void;
    /** Plain divided rows without the helper line; the section shows one legend instead. */
    compact?: boolean;
}

const LIST_CLASS = { regular: 'flex flex-col gap-2', compact: 'divide-y divide-gray-100' } as const;
const ROW_CLASS = {
    regular: 'flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2',
    compact: 'flex items-center gap-3 py-2 first:pt-0'
} as const;

// Placed elastics beneath the chart (name, tooth loop, size, reorder, remove); read-only
// recaps render the same rows as static text.
export function PlacedElasticsList({
    placements,
    readOnly,
    drawing,
    onStartDrawing,
    onClearAll,
    onRemove,
    onSizeChange,
    onWearChange,
    onNameChange,
    onReorder,
    compact = false
}: PlacedElasticsListProps) {
    const missingSize = placements.some((p) => p.size.trim().length === 0);
    const variant = compact ? 'compact' : 'regular';

    return (
        <div>
            <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                    Placed elastics
                </span>
                {!readOnly && (
                    <div className="flex items-center gap-3">
                        {!drawing && (
                            <button
                                type="button"
                                onClick={onStartDrawing}
                                className="flex items-center gap-1.5 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-gray-800"
                            >
                                <PlusIcon className="h-3.5 w-3.5" />
                                Add elastic
                            </button>
                        )}
                        {placements.length > 0 && (
                            <button
                                type="button"
                                onClick={onClearAll}
                                className="text-xs font-medium text-gray-400 hover:text-red-600"
                            >
                                Clear all
                            </button>
                        )}
                    </div>
                )}
            </div>

            {placements.length === 0 ? (
                <p className="text-xs italic text-gray-400">No elastics placed yet.</p>
            ) : (
                <>
                    {!compact && (
                        <p className="mb-2 text-[11px] text-gray-400">
                            Numbered by install order · red circle = buccal, blue triangle = lingual
                            {!readOnly && ' (click a button to switch)'} · arrow = direction
                            {!readOnly && ' · right-click a band to delete it'}.
                        </p>
                    )}
                    {!readOnly && missingSize && (
                        <p className="mb-2 text-[11px] font-medium text-red-600">
                            Every elastic needs a size before you can continue.
                        </p>
                    )}
                    <ul className={LIST_CLASS[variant]}>
                        {placements.map((placement, index) => {
                            const color = placementColor(placement);
                            return (
                                <li key={placement.id} className={ROW_CLASS[variant]}>
                                    <span
                                        className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
                                        style={{ backgroundColor: color }}
                                    >
                                        {index + 1}
                                    </span>
                                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                                        {readOnly ? (
                                            <span className="text-sm font-medium text-gray-800">
                                                {placement.name.trim() || `Elastic ${index + 1}`}
                                            </span>
                                        ) : (
                                            <input
                                                type="text"
                                                value={placement.name}
                                                onChange={(e) =>
                                                    onNameChange(placement.id, e.target.value)
                                                }
                                                placeholder={`Elastic ${index + 1}`}
                                                maxLength={200}
                                                className="w-full max-w-[220px] rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-800 placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                            />
                                        )}
                                        <span className="text-xs text-gray-500">
                                            <span className="font-semibold text-gray-700">
                                                {toothWithSide(placement, placement.teeth[0])}
                                            </span>
                                            {placement.teeth.length > 1 &&
                                                ` → ${placement.teeth
                                                    .slice(1)
                                                    .map((f) => toothWithSide(placement, f))
                                                    .join(' → ')}`}
                                        </span>
                                    </div>
                                    {readOnly ? (
                                        <span className="flex flex-shrink-0 flex-col items-end">
                                            <span className="text-xs text-gray-600">
                                                {placement.size || '-'}
                                            </span>
                                            {placement.wear && (
                                                <span className="text-xs text-gray-600">
                                                    {placement.wear}
                                                </span>
                                            )}
                                        </span>
                                    ) : (
                                        <>
                                            <label className="flex flex-shrink-0 flex-col text-[11px] font-medium text-gray-500">
                                                Size
                                                <select
                                                    value={placement.size}
                                                    onChange={(e) =>
                                                        onSizeChange(placement.id, e.target.value)
                                                    }
                                                    className={`${baseFieldClasses} mt-1 h-8 w-40 appearance-none py-0 text-xs ${
                                                        placement.size
                                                            ? ''
                                                            : 'border-red-300 text-gray-400 focus:border-red-500 focus:ring-red-500'
                                                    }`}
                                                >
                                                    <option value="">Select…</option>
                                                    {ELASTIC_SIZE_OPTIONS.map((opt) => (
                                                        <option
                                                            key={opt}
                                                            value={opt}
                                                            className="text-gray-800"
                                                        >
                                                            {opt}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="flex flex-shrink-0 flex-col text-[11px] font-medium text-gray-500">
                                                Daily wear
                                                <select
                                                    value={placement.wear}
                                                    onChange={(e) =>
                                                        onWearChange(placement.id, e.target.value)
                                                    }
                                                    className={`${baseFieldClasses} mt-1 h-8 w-32 appearance-none py-0 text-xs`}
                                                >
                                                    {ELASTIC_WEAR_OPTIONS.map((opt) => (
                                                        <option
                                                            key={opt}
                                                            value={opt}
                                                            className="text-gray-800"
                                                        >
                                                            {opt}
                                                        </option>
                                                    ))}
                                                </select>
                                            </label>
                                        </>
                                    )}
                                    {!readOnly && placements.length > 1 && (
                                        <span className="flex flex-shrink-0 flex-col">
                                            <button
                                                type="button"
                                                onClick={() => onReorder(placement.id, -1)}
                                                disabled={index === 0}
                                                className="rounded p-0.5 text-gray-400 hover:text-gray-800 disabled:opacity-30 disabled:hover:text-gray-400"
                                                aria-label="Move earlier"
                                            >
                                                <ChevronUpIcon className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => onReorder(placement.id, 1)}
                                                disabled={index === placements.length - 1}
                                                className="rounded p-0.5 text-gray-400 hover:text-gray-800 disabled:opacity-30 disabled:hover:text-gray-400"
                                                aria-label="Move later"
                                            >
                                                <ChevronDownIcon className="h-3.5 w-3.5" />
                                            </button>
                                        </span>
                                    )}
                                    {!readOnly && (
                                        <button
                                            type="button"
                                            onClick={() => onRemove(placement.id)}
                                            className="flex-shrink-0 rounded p-0.5 text-gray-400 hover:text-red-600"
                                            aria-label="Remove elastic"
                                        >
                                            <XMarkIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </>
            )}
        </div>
    );
}
